import { useCallback, useEffect, useState, FocusEvent } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { createPaymentScheduleSchema, PaymentScheduleForm } from "./paymentSchedule.schema";
import { FormField, FormItem, FormLabel, FormControl } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Button } from "@/src/components/ui/button";
import { TrashIcon } from "lucide-react";
import { Fragment } from "react";
import { transformCustomerPaymentSchedule } from "./transformCustomerPaymentSchedule";
import { useToast } from "@/src/components/ui/use-toast";
import { useUpdatePaymentSchedule } from "@/src/lib/paymentScheduleMutation";
import { useParams } from "react-router-dom";
import { Card } from "@/src/components/wrappers/Card";
import { Content } from "@/src/components/wrappers/Content";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { DatePicker } from "@/src/components/ui/date-picker";
import { differenceInDays, format, startOfDay, subDays } from "date-fns";
import OperatorForm from "./OperatorForm";

const EditPaymentSchedule = () => {
  const { id: bookingId } = useParams();
  const { toast } = useToast();
  const { data: paymentScheduleCustomerData } = useQuery({
    queryKey: ["paymentScheduleCustomerData", bookingId],
    queryFn: () =>
      paymentsFetch<{ data: any[] }>(
        `/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=Customer&bookingRef=${bookingId}&operatorRef=null`,
      ).then((res) => res),
    enabled: !!bookingId,
  });
  const mutation = useUpdatePaymentSchedule(bookingId ?? "");

  const totalAmount =
    paymentScheduleCustomerData?.data?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 1;

  const [lastEditedField, setLastEditedField] = useState<"amount" | "percentage" | null>(null);

  const form = useForm<PaymentScheduleForm>({
    resolver: zodResolver(createPaymentScheduleSchema(totalAmount, lastEditedField)),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      paymentSchedule: [],
    },
  });

  const { fields, append, replace } = useFieldArray({
    control: form.control,
    name: "paymentSchedule",
    keyName: "id",
  });

  useEffect(() => {
    if (paymentScheduleCustomerData?.data) {
      const transformedData = transformCustomerPaymentSchedule(paymentScheduleCustomerData.data);
      replace(transformedData);
    }
  }, [paymentScheduleCustomerData, replace]);

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        const toastId = toast({
          description: "Updating payment schedule...",
          variant: "default",
          duration: Infinity,
        }).id;
        mutation.mutate({ paymentSchedule: data.paymentSchedule, toastId });
      },
      () => {
        const values = form.getValues();
        const issues = createPaymentScheduleSchema(totalAmount, lastEditedField).safeParse(values).error?.issues ?? [];

        if (issues.some((issue) => issue.message === "Total percentage must be exactly 100%")) {
          toast({
            title: "Error",
            description: "Total sum of percantages should be 100!",
            variant: "destructive",
          });
          values.paymentSchedule.forEach((_, i) => {
            form.setError(`paymentSchedule.${i}.amount`, {
              type: "manual",
            });
          });
        } else if (issues.some((issue) => issue.message === "Days are required")) {
          toast({
            title: "Error",
            description: "Days are required!",
            variant: "destructive",
          });
          values.paymentSchedule.forEach((_, i) => {
            form.setError(`paymentSchedule.${i}.days`, {
              type: "manual",
            });
          });
        }

        console.error("Form validation errors:", form.formState.errors);
      },
    ),
    [form],
  );

  const handleRemove = (index: number) => {
    const currentValues = form.getValues("paymentSchedule");

    const removedItem = currentValues.find((_, i) => i === index);
    if (!removedItem) return;

    const updatedFields = currentValues.filter((_, i) => i !== index);

    if (updatedFields.length === 0) {
      form.setValue("paymentSchedule", []);
      return;
    }

    form.setValue("paymentSchedule", updatedFields, { shouldDirty: true, shouldTouch: true });
  };

  const bookingDate = paymentScheduleCustomerData?.data?.[0]?.booking?.bookingDateFrom;

  const blockScroll = (e: FocusEvent<HTMLInputElement>) =>
    e.target?.addEventListener(
      "wheel",
      function (e) {
        e.preventDefault();
      },
      { passive: false },
    );

  const normalizeDate = (dateString: string): Date => {
    const parsedDate = new Date(dateString);

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
  };

  const countDaysBeforeBooking = (dueDate: Date) => {
    const bd = startOfDay(new Date(bookingDate));
    const dd = startOfDay(new Date(dueDate));
    const diff = differenceInDays(dd, bd);
    return Math.abs(diff);
  };

  return (
    <Content
      title={"Edit payment schedule - " + (paymentScheduleCustomerData?.data?.[0]?.booking?.yacht?.name ?? "booking")}
      preserveQuery
    >
      <Card className="w-full flex flex-col gap-y-6">
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <>
              <h3 className="text-lg font-semibold col-span-full mb-4">
                Payment Schedule Customer's
                <span className="font-normal">(charter date: {bookingDate && formatDateUTC(bookingDate, "UTC")})</span>
              </h3>
            </>
            <div className="grid gap-4 grid-cols-4 max-w-[1200px]">
              {fields.map((step, i) => {
                return (
                  <Fragment key={step.id}>
                    <FormField
                      key={step.id}
                      control={form.control}
                      name={`paymentSchedule.${i}.amount`}
                      render={({ field: { value, onChange, ...props }, fieldState: { error } }) => {
                        return (
                          <FormItem required>
                            <FormLabel>Amount</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                step={0.01}
                                onWheel={(e) => e.currentTarget.blur()}
                                {...props}
                                value={value ?? ""}
                                onChange={(e) => {
                                  setLastEditedField("amount");

                                  const newAmount = parseFloat(e.target.value) || "";
                                  onChange(newAmount);
                                }}
                              />
                            </FormControl>
                            {error && <p className="text-red-500 text-sm">{error.message}</p>}
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      name={`paymentSchedule.${i}.dueDate`}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Due date</FormLabel>
                            <FormControl>
                              <DatePicker
                                disabledDays={{ after: new Date(bookingDate) }}
                                className="w-full"
                                onChange={(v) => {
                                  if (v) {
                                    const formattedDate = format(v, "yyyy-MM-dd");
                                    field.onChange(v ? formattedDate : null);
                                    const days = countDaysBeforeBooking(v);
                                    form.setValue(`paymentSchedule.${i}.days`, days);

                                    return v;
                                  } else {
                                    field.onChange(null);
                                    return "";
                                  }
                                }}
                                value={field.value ? normalizeDate(field.value) : undefined}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name={`paymentSchedule.${i}.days`}
                      render={({ field }) => (
                        <FormItem required>
                          <FormLabel>Days</FormLabel>
                          <FormControl>
                            <Input
                              onFocus={blockScroll}
                              {...field}
                              type="number"
                              onChange={(e) => {
                                const raw = e.target.value;

                                field.onChange(+raw || "");
                                if (raw === "0") field.onChange(0);

                                const value = +raw;

                                if (raw !== "" && !isNaN(value)) {
                                  const bd = startOfDay(new Date(bookingDate));
                                  const newDueDate = subDays(bd, value);
                                  const formatted = format(newDueDate, "yyyy-MM-dd");
                                  form.setValue(`paymentSchedule.${i}.dueDate`, formatted);
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`paymentSchedule.${i}.type`}
                      render={({ field }) => (
                        <FormItem required>
                          <FormLabel>Payment Time</FormLabel>
                          <div className="flex gap-2 items-end w-full">
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="before booking date">Before Charter Date</SelectItem>
                              </SelectContent>
                            </Select>
                            {fields.length > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => handleRemove(i)}>
                                <TrashIcon size={16} />
                              </Button>
                            )}
                          </div>
                        </FormItem>
                      )}
                    />
                  </Fragment>
                );
              })}
              <div className="flex gap-2">
                <Button className="col-start-1" variant="default">
                  Submit
                </Button>
                <Button
                  onClick={() => {
                    append({ stepId: "", amount: undefined, days: undefined, type: "before booking date" });
                  }}
                  className="col-start-1"
                  type="button"
                  variant="secondary"
                >
                  Add Another Step
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
      <OperatorForm />
    </Content>
  );
};

export default EditPaymentSchedule;
